import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CellInfo } from 'react-table';
import { StatusKeys } from '../../components/status-select/types';
import { ViewFrame, ListTable } from '../../components';
import { useTitle, useActionDispatcher } from '../../hooks';
import {
  deleteProjectRequest,
  getProjectListRequest,
  resetCurrentProject,
  setProjectListDirty,
} from '../../store/modules/projects/actions';
import { selectProjectList } from '../../store/modules/projects/selectors';
import { selectCurrentUser } from '../../store/modules/users/selectors';
import DeleteAction from '../../components/action/DeleteAction';
import DuplicateAction from './actions/duplicateAction';
import ProjectListRowHotKeyListener from './actions/ProjectListRowHotKeyListener';
import { getAllQueryData, updateQuery } from '../../utility/helpers';
import AdvancedActionCell from '../../components/action-cell/AdvancedActionCell';
import StatusSelectOption from '../../components/status-select/StatusSelectOption';
import CopyProjectInfoAction from './actions/CopyProjectInfoAction';

const title = 'Projects';

const ProjectList = () => {
  useTitle(title);
  const dispatch = useDispatch();
  const { data: user } = useSelector(selectCurrentUser);
  const { loading, data, dirty } = useSelector(selectProjectList);

  const getProjectList = useActionDispatcher(getProjectListRequest);
  const setDirty = useActionDispatcher(setProjectListDirty);
  const deleteProject = useActionDispatcher(deleteProjectRequest);

  const queryData = getAllQueryData();
  if (user && !data && !queryData.filter) {
    updateQuery({
      filter: { user_owner_id: user.id },
    });
  }

  useEffect(() => {
    dispatch(resetCurrentProject());
  }, [dispatch]);

  return (
    <ViewFrame title={title}>
      <ProjectListRowHotKeyListener />
      <ListTable
        columns={[
          {
            id: 'title',
            Header: 'Title',
          },
          {
            id: 'ref.user_owner.full_name_first_last',
            Header: 'Owner',
            width: 160,
          },
          {
            id: 'ref.organization.common_name',
            Header: 'Organization',
            width: 130,
          },
          {
            id: 'ref.status.description',
            Header: 'Status',
            width: 130,
            Cell: (cell: CellInfo) => {
              const { status } = cell.original.ref;
              if (status) {
                return (
                  <StatusSelectOption title={status.description} statusKey={status.id as StatusKeys} />
                );
              }
              return 'Status';
            },
          },
          {
            id: 'ref.updated_age',
            Header: 'Updated',
            width: 100,
          },
          {
            id: 'actions',
            accessor: 'id',
            Cell: (cell: CellInfo) => (
              <AdvancedActionCell zIndex={2147483099}>
                {
                  cell.original.permissions.can_view
                  && <DuplicateAction original={cell.original} />
                }
                {
                  cell.original.permissions.can_delete
                  && <DeleteAction {...cell} onDelete={deleteProject} />
                }
                {
                  user?.is_super_admin
                  && <CopyProjectInfoAction project={cell.original} />
                }
              </AdvancedActionCell>
            ),
            width: 65,
          },
        ]}
        loading={loading}
        data={data}
        fetch={getProjectList}
        dirty={Boolean(dirty)}
        setDirty={setDirty}
      />

    </ViewFrame>
  );
};

export default ProjectList;
